import { ArrowRight, Bell, CheckCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { fmtRelative } from '../shared/format'
import { useMarkAllRead, useNotificationList, useSetRead } from './api'
import { CATEGORY_ICON, LEVEL_TONE } from './visuals'

export function NotificationBell() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const list = useNotificationList().data ?? []
  const setRead = useSetRead()
  const markAll = useMarkAllRead()
  const unread = list.filter((n) => !n.readAt).length
  const recent = list.slice(0, 6)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={unread ? t('notifications.bellUnread', { count: unread }) : t('common.notifications')} className="relative rounded-xl text-muted-foreground hover:text-foreground">
          <Bell />
          {unread > 0 ? (
            <span className="pointer-events-none absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-brand-orange px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-card">{unread > 9 ? '9+' : unread}</span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" collisionPadding={12} className="w-[calc(100vw-1.5rem)] p-0 sm:w-[22rem]">
        <div className="flex items-center justify-between gap-2 px-3 py-2.5">
          <div>
            <p className="text-sm font-semibold">{t('common.notifications')}</p>
            <p className="text-xs text-muted-foreground">{unread ? t('notifications.unreadCount', { count: unread }) : t('notifications.allRead')}</p>
          </div>
          {unread ? (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => markAll.mutate()} disabled={markAll.isPending}>
              <CheckCheck />
              {t('notifications.markAllRead')}
            </Button>
          ) : null}
        </div>
        <DropdownMenuSeparator className="my-0" />
        {recent.length === 0 ? (
          <p className="px-3 py-8 text-center text-sm text-muted-foreground">{t('notifications.empty')}</p>
        ) : (
          <div className="max-h-[22rem] overflow-y-auto py-1">
            {recent.map((n) => {
              const Icon = CATEGORY_ICON[n.category]
              return (
                <DropdownMenuItem
                  key={n.id}
                  className={cn('mx-1 items-start gap-3 rounded-lg px-2 py-2', !n.readAt && 'bg-primary/[0.04]')}
                  onSelect={() => {
                    if (!n.readAt) setRead.mutate({ id: n.id, read: true })
                    if (n.link) navigate(n.link)
                  }}
                >
                  <span className={cn('mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-lg', LEVEL_TONE[n.level])}>
                    <Icon className="size-4" aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className={cn('truncate text-sm', !n.readAt ? 'font-semibold' : 'font-medium')}>{n.title}</span>
                      {!n.readAt ? <span className="size-2 shrink-0 rounded-full bg-brand-orange" aria-label={t('notifications.unread')} /> : null}
                    </span>
                    <span className="line-clamp-2 block text-xs text-muted-foreground">{n.body}</span>
                    <span className="mt-0.5 block text-[11px] text-muted-foreground">{fmtRelative(n.createdAt)}</span>
                  </span>
                </DropdownMenuItem>
              )
            })}
          </div>
        )}
        <DropdownMenuSeparator className="my-0" />
        <div className="p-2">
          <Button variant="outline" size="sm" className="w-full" onClick={() => navigate('/notifications')}>
            {t('notifications.seeAll')}
            <ArrowRight />
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
